import { useQuery, useQueryClient } from "@tanstack/react-query"
import React, { createContext, useContext, useEffect, useState } from "react"

import { useAppContext } from "@contexts/app_context"
import {
  getLiveChatWidgetOverview,
  getLiveChatWidgetRelease,
  getLiveChatWidgetReleases,
  invalidateLiveChatWidgetCache,
  publishLiveChatWidgetRelease,
  rollbackLiveChatWidgetRelease
} from "@domain/live_chat_widget"
import type {
  LiveChatWidgetOverview,
  LiveChatWidgetRelease,
  PublishLiveChatWidgetReleaseInput
} from "@models/live_chat_widget"
import { showSuccessToast } from "@utils/toast"

const OVERVIEW_QUERY_KEY = ["live-chat-widget-overview"]
const RELEASES_QUERY_KEY = ["live-chat-widget-releases"]

/** A CloudFront `/*` invalidation normally settles in one to three minutes. */
const INVALIDATION_POLL_MS = 8000

type LiveChatWidgetPageContextType = {
  translate: (key: string) => string
  overview?: LiveChatWidgetOverview
  releases: LiveChatWidgetRelease[]
  isLoading: boolean
  busy: boolean
  /** The release whose invalidation is still being polled, if any. */
  pendingRelease?: LiveChatWidgetRelease
  publish: (input: PublishLiveChatWidgetReleaseInput) => Promise<boolean>
  rollback: (releaseId: string) => Promise<boolean>
  invalidateCache: () => Promise<boolean>
}

const LiveChatWidgetPageContext = createContext<LiveChatWidgetPageContextType>({
  translate: (key: string) => key,
  releases: [],
  isLoading: false,
  busy: false,
  publish: () => Promise.resolve(false),
  rollback: () => Promise.resolve(false),
  invalidateCache: () => Promise.resolve(false)
})

export function LiveChatWidgetPageContextProvider(props: {
  children: JSX.Element | JSX.Element[]
}) {
  const { translate } = useAppContext()
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [trackedReleaseId, setTrackedReleaseId] = useState<string | null>(null)

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: OVERVIEW_QUERY_KEY,
    queryFn: async () => (await getLiveChatWidgetOverview()).data ?? undefined
  })

  const { data: releases, isLoading: releasesLoading } = useQuery({
    queryKey: RELEASES_QUERY_KEY,
    queryFn: async () => (await getLiveChatWidgetReleases()).data ?? []
  })

  // The API flips INVALIDATING -> ACTIVE only when this query re-reads
  // CloudFront, so polling it is what advances the release, not just the UI.
  const { data: pendingRelease } = useQuery({
    queryKey: ["live-chat-widget-release", trackedReleaseId],
    queryFn: async () =>
      (await getLiveChatWidgetRelease(trackedReleaseId as string)).data ??
      undefined,
    enabled: !!trackedReleaseId,
    refetchInterval: INVALIDATION_POLL_MS
  })

  useEffect(() => {
    if (!pendingRelease || pendingRelease.status === "INVALIDATING") return
    setTrackedReleaseId(null)
    void queryClient.invalidateQueries({ queryKey: OVERVIEW_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: RELEASES_QUERY_KEY })
  }, [pendingRelease, queryClient])

  const refreshAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: OVERVIEW_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: RELEASES_QUERY_KEY })
    ])
  }

  // Errors are surfaced by `mutate`/`query` in @utils/graphql, so these only
  // handle the success path and return whether the caller should reset itself.
  const publish = async (input: PublishLiveChatWidgetReleaseInput) => {
    setBusy(true)
    const response = await publishLiveChatWidgetRelease(input)
    setBusy(false)
    if (!response.isSuccess || !response.data) return false

    showSuccessToast(translate("live_chat_widget_published"))
    setTrackedReleaseId(response.data._id)
    await refreshAll()
    return true
  }

  const rollback = async (releaseId: string) => {
    setBusy(true)
    const response = await rollbackLiveChatWidgetRelease({ releaseId })
    setBusy(false)
    if (!response.isSuccess || !response.data) return false

    showSuccessToast(translate("live_chat_widget_rolled_back"))
    setTrackedReleaseId(response.data._id)
    await refreshAll()
    return true
  }

  const invalidateCache = async () => {
    setBusy(true)
    const response = await invalidateLiveChatWidgetCache()
    setBusy(false)
    if (!response.isSuccess) return false

    showSuccessToast(translate("live_chat_widget_cache_invalidated"))
    return true
  }

  return (
    <LiveChatWidgetPageContext.Provider
      value={{
        translate,
        overview,
        releases: releases ?? [],
        isLoading: overviewLoading || releasesLoading,
        busy,
        pendingRelease: trackedReleaseId ? pendingRelease : undefined,
        publish,
        rollback,
        invalidateCache
      }}
    >
      {props.children}
    </LiveChatWidgetPageContext.Provider>
  )
}

export function useLiveChatWidgetPageContext() {
  return useContext(LiveChatWidgetPageContext)
}
